/**
 * One notation for money everywhere in control testing: the run's currency symbol (or its code) and international
 * grouping, e.g. "₹13,376.00". The currency is the one marked most in the samples and evidence (set by the AI
 * service); with no currency the plain number is shown — never a guessed "$".
 */

const SYMBOL: Record<string, string> = { INR: '₹', USD: '$', EUR: '€', GBP: '£', JPY: '¥' };

/** "₹" / "$" / "AED" for a currency code, '' when there is none. */
export function currencyMark(currency?: string | null): string {
  if (!currency) return '';
  const code = currency.toUpperCase();
  return SYMBOL[code] || code;
}

/** A column-header suffix: " (₹)" / " (AED)", '' when there is no currency. */
export function currencySuffix(currency?: string | null): string {
  const mark = currencyMark(currency);
  return mark ? ` (${mark})` : '';
}

/** A number from an amount as printed ("Rs. 4,294.02", "₹13,376.00", "(1,250.00)"), or null. */
export function parseMoney(value: unknown): number | null {
  if (value == null || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  // Currency marks and CR/DR first ("Rs.", "INR", "₹", "US$"), so their dots are not read as decimal points.
  let text = String(value).trim()
    .replace(/^(?:Rs\.?|INR|USD|EUR|GBP|AED|SGD|US\$|S\$|A\$|C\$)\s*/i, '')
    .replace(/[₹$€£¥]/g, '')
    .replace(/\s*(?:CR|DR)$/i, '')
    .trim();
  let negative = false;
  if (/^\(.*\)$/.test(text)) { negative = true; text = text.slice(1, -1).trim(); }
  if (/^[-\u2212\u2013]/.test(text)) { negative = true; text = text.slice(1).trim(); }
  else if (/[-\u2212\u2013]$/.test(text)) { negative = true; text = text.slice(0, -1).trim(); }
  let number: string | null = null;
  if (/^\d{1,3}(,\d{2,3})+(\.\d+)?$/.test(text)) number = text.replace(/,/g, '');      // 13,376.00
  else if (/^\d+(\.\d+)?$/.test(text)) number = text;                                     // 4294.02
  else if (/^\d{1,3}([.\s]\d{3})+,\d{1,2}$|^\d+,\d{1,2}$/.test(text)) {                 // 1.234,56 / 1 234,56
    number = text.replace(/[.\s]/g, '').replace(',', '.');
  }
  if (number == null) return null;                                                       // a date, an id, words
  const n = Number(number);
  if (!Number.isFinite(n)) return null;
  return negative && n > 0 ? -n : n;
}

interface MoneyOptions {
  decimals?: number;      // fixed decimals (default 2)
  maxDecimals?: number;   // when set, trailing zero decimals are dropped down to this precision
  sign?: boolean;         // show "+" for positive values (differences)
  empty?: string;         // shown for a missing value (default "—")
}

/** "₹13,376.00" / "-$1,250.00" / "13,376.00" (no currency). Text that is not a number is returned as-is. */
export function formatMoney(value: unknown, currency?: string | null, opts: MoneyOptions = {}): string {
  const { decimals = 2, maxDecimals, sign = false, empty = '—' } = opts;
  if (value == null || value === '') return empty;
  const n = parseMoney(value);
  if (n == null) return String(value);
  const body = Math.abs(n).toLocaleString('en-US', maxDecimals != null
    ? { minimumFractionDigits: 0, maximumFractionDigits: maxDecimals }
    : { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  const mark = currencyMark(currency);
  const spacer = mark && mark.length > 1 ? ' ' : '';
  const prefix = n < 0 ? '−' : sign ? '+' : '';
  return `${prefix}${mark}${spacer}${body}`;
}
